// Turns the JSON that a Devise endpoint sends back with a failed request into
// a message that can be shown to the user, e.g. in a flash alert.
//
// Plain JavaScript on purpose (no React or Redux) so that it is easy to test.
//
// The server answers a failure in one of two shapes. They are pinned by
// test/integration/devise_json_errors_test.rb, so update both together:
//
//   422  { errors: { email: ['not found'] } }        messages by field
//   401  { error: 'Invalid login or password.' }     a single message
//
// Anything else (no body, an unexpected shape) gets a generic message, so that
// the user always sees something.

const GENERIC_MESSAGE = 'Something went wrong. Please try again.'
const ERRORS_HEADING =
  'The following errors prevented this request from being processed:'

// Field names whose label isn't just the capitalised name.
const LABEL_OVERRIDES = { recaptcha: 'reCAPTCHA' }

// 'reset_password_token' becomes 'Reset Password Token'.
const labelFor = (field) =>
  LABEL_OVERRIDES[field] ??
  field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// The messages for one field, ready for a TextField's helperText:
// "Can't be blank", or undefined if the field has no errors.
export function fieldErrorText(fieldErrors, field) {
  const messages = fieldErrors?.[field]
  if (!messages || messages.length === 0) {
    return undefined
  }
  const text = messages.join(', ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Splits a 422's errors into the ones a screen shows beside its own fields
// (`fields`, e.g. ['email']) and the flash message that goes with them. The
// flash always says that a problem happened, so the pattern is the same on
// every screen: the flash tells the user something went wrong, and the field
// messages, where there are any, say what.
//
//   fieldErrors   { field: [messages] } for the fields the screen has
//   flashMessage  when the fields show every error, a short summary such as
//                 "One error prevented the password from being changed"
//                 (`whatFailed` finishes that sentence); otherwise the message
//                 for the errors with no field on this screen, or the generic
//                 message for a body that isn't the { errors } shape
export function splitFieldErrors(json, fields, whatFailed = 'this request from being processed') {
  const fieldErrors = {}
  const otherErrors = {}

  const errors = json?.errors
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    Object.entries(errors).forEach(([field, messages]) => {
      const target = fields.includes(field) ? fieldErrors : otherErrors
      target[field] = [].concat(messages)
    })
  }

  const hasFieldErrors = Object.keys(fieldErrors).length > 0
  const hasOtherErrors = Object.keys(otherErrors).length > 0
  if (hasFieldErrors && !hasOtherErrors) {
    const count = Object.values(fieldErrors).flat().length
    const errorCount = count > 1 ? `${count} errors` : 'One error'
    return { fieldErrors, flashMessage: `${errorCount} prevented ${whatFailed}` }
  }
  return {
    fieldErrors,
    flashMessage: formatDeviseErrors(hasOtherErrors ? { errors: otherErrors } : json),
  }
}

export function formatDeviseErrors(json) {
  if (!json || typeof json !== 'object') {
    return GENERIC_MESSAGE
  }

  if (typeof json.error === 'string' && json.error) {
    return json.error
  }

  const errors = json.errors
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const lines = Object.entries(errors).map(
      ([field, messages]) => `• ${labelFor(field)}: ${[].concat(messages).join(', ')}`
    )
    if (lines.length > 0) {
      return [ERRORS_HEADING, ...lines].join('\n')
    }
  }

  return GENERIC_MESSAGE
}
